import React from 'react';

/**
 * DuckPilot brand mark — "the confident duck" (Design Brief, thesis 1a).
 * One circle (head), one bill (the single terracotta moment), one knocked-out eye.
 *
 * Variants:
 *   - 'default'  head=ink, bill=terracotta, eye=paper  (on light surfaces)
 *   - 'reversed' all paper                              (on ink / dark surfaces)
 *   - 'mono'     head+bill=ink, eye=paper               (small / print)
 *   - 'muted'    all sand-600                           (footnotes)
 */
export function DuckMark({ size = 32, variant = 'default', className = '', style }) {
  const palettes = {
    default: { head: '#201e1d', bill: '#c67139', eye: '#f5ead8' },
    reversed: { head: '#f5ead8', bill: '#f5ead8', eye: '#201e1d' },
    mono: { head: '#201e1d', bill: '#201e1d', eye: '#f5ead8' },
    muted: { head: '#82796a', bill: '#82796a', eye: '#ffffff' },
    sage: { head: '#3d472b', bill: '#3d472b', eye: '#f0fae1' },
  };
  const c = palettes[variant] || palettes.default;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      className={className}
      style={style}
      role="img"
      aria-label="DuckPilot"
    >
      <circle cx="20" cy="24" r="13" fill={c.head} />
      <rect x="29" y="20" width="15" height="9" rx="4.5" fill={c.bill} />
      <circle cx="24" cy="18.5" r="2.6" fill={c.eye} />
    </svg>
  );
}

/**
 * Horizontal lockup: mark + Caprasimo wordmark.
 * `tone` sets the wordmark color; the mark variant follows automatically.
 */
export function DuckPilotWordmark({
  size = 28,
  fontSize = 19,
  tone = 'ink',
  gap = 10,
  className = '',
}) {
  const wordColor = tone === 'paper' ? '#f5ead8' : '#201e1d';
  const markVariant = tone === 'paper' ? 'reversed' : 'default';
  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap }}
    >
      <DuckMark size={size} variant={markVariant} />
      <span
        className="font-display"
        style={{
          fontSize,
          letterSpacing: '-0.01em',
          color: wordColor,
          lineHeight: 1,
        }}
      >
        DuckPilot
      </span>
    </span>
  );
}

export default DuckMark;
